import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { securityService } from '../services/payment/securityService';

interface SecurityContextValue {
  pinEnabled: boolean;
  biometricsEnabled: boolean;
  twoFactorEnabled: boolean;
  /** Whether the current high-value action has been authorised this session. */
  unlocked: boolean;
  setPinEnabled: (v: boolean) => void;
  setBiometricsEnabled: (v: boolean) => void;
  setTwoFactorEnabled: (v: boolean) => void;
  verifyPin: (pin: string) => Promise<boolean>;
  authenticateBiometric: () => Promise<boolean>;
  unlock: () => void;
}

const SecurityContext = createContext<SecurityContextValue | undefined>(undefined);

export function SecurityProvider({ children }: { children: ReactNode }) {
  const [pinEnabled, setPinEnabled] = useState(true);
  const [biometricsEnabled, setBiometricsEnabled] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const verifyPin = useCallback(async (pin: string) => {
    const res = await securityService.verifyPin(pin);
    const valid = res.ok && res.data.valid;
    if (valid) setUnlocked(true);
    return valid;
  }, []);

  const authenticateBiometric = useCallback(async () => {
    const res = await securityService.authenticateBiometric();
    const ok = res.ok && res.data.success;
    if (ok) setUnlocked(true);
    return ok;
  }, []);

  const unlock = useCallback(() => setUnlocked(true), []);

  const value = useMemo<SecurityContextValue>(
    () => ({
      pinEnabled,
      biometricsEnabled,
      twoFactorEnabled,
      unlocked,
      setPinEnabled,
      setBiometricsEnabled,
      setTwoFactorEnabled,
      verifyPin,
      authenticateBiometric,
      unlock,
    }),
    [pinEnabled, biometricsEnabled, twoFactorEnabled, unlocked, verifyPin, authenticateBiometric, unlock],
  );

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
}

export function useSecurityContext() {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error('useSecurityContext must be used within a SecurityProvider');
  return ctx;
}
